/**
 * F5 subtitle editor — the pure state machine (tested; no DOM). The component
 * (`components/watch/TranscriptEditor.tsx`) is thin over this: it calls the server per line,
 * feeds every `TranscriptMutationResponse` through `applyMutation`, keeps an undo stack of
 * `inverseOf` entries, and classifies errors with `classifyEditError`.
 *
 * Model (M3-HANDOFF "Constraints"): stable line ids, per-line commits, the transcript `revision`
 * threaded through every call. PATCH keeps the id; split keeps the original as the first half
 * and returns `[first, second]`; merge returns `[merged]` (the next row is gone); insert returns
 * `[new]`; delete returns no rows (the caller removes by id). The client re-sorts by `idx`.
 */
import type {
  Job,
  Line,
  LineUpdate,
  Problem,
  TranscriptMutationResponse,
  TranslationStatus,
} from '@nelnela/contracts';

export type MutationKind = 'update' | 'split' | 'merge' | 'insert' | 'delete';

export interface MutationOp {
  kind: MutationKind;
  /** the row the op was issued on (delete: the row that is gone) */
  lineId?: string;
  /** rows the server dropped and did not echo (merge: the absorbed next row; delete: the row) */
  removedIds?: string[];
}

/** Stable order: by idx, ties by startMs (idx gaps after merge/delete are fine). */
export function sortLines(lines: readonly Line[]): Line[] {
  return [...lines].sort((a, b) => a.idx - b.idx || a.startMs - b.startMs);
}

/**
 * Merge the rows a mutation returned into the list by id (replace or add), drop `removedIds`
 * (and the deleted row itself), and re-sort. Pure — never mutates the input.
 *
 * Split/insert make room server-side (`UPDATE line SET idx = idx + 1 WHERE idx > …`) but echo
 * only the new/changed rows, so the following rows' client idx would fall behind the server's
 * and the next structural edit nearby would misorder them. Mirror the shift here: for each
 * newly inserted row with idx N, every pre-existing row the response did not echo with
 * idx >= N moves down one. Merge/delete leave gaps and need nothing.
 */
export function applyMutation(
  lines: readonly Line[],
  resp: TranscriptMutationResponse,
  op: MutationOp,
): Line[] {
  const removed = new Set(op.removedIds ?? []);
  if (op.kind === 'delete' && op.lineId) removed.add(op.lineId);
  const existingIds = new Set(lines.map((l) => l.id));
  const echoed = new Set(resp.lines.map((l) => l.id));
  const insertedIdx = resp.lines
    .filter((l) => !existingIds.has(l.id) && !removed.has(l.id))
    .map((l) => l.idx)
    .sort((a, b) => a - b);
  const byId = new Map<string, Line>();
  for (const l of lines) {
    if (removed.has(l.id)) continue;
    if (echoed.has(l.id) || insertedIdx.length === 0) {
      byId.set(l.id, l);
      continue;
    }
    let idx = l.idx;
    for (const n of insertedIdx) if (idx >= n) idx++;
    byId.set(l.id, idx === l.idx ? l : { ...l, idx });
  }
  for (const l of resp.lines) if (!removed.has(l.id)) byId.set(l.id, l);
  return sortLines([...byId.values()]);
}

/** A restore/reload answers with the whole transcript: replace, don't merge. */
export function replaceAll(resp: TranscriptMutationResponse): Line[] {
  return sortLines(resp.lines);
}

// ---------------------------------------------------------------- undo

export type LinePatch = Omit<LineUpdate, 'revision'>;

/** What re-inserting a deleted row needs (LineInsertRequest has no translation — patched after). */
export interface InsertContent {
  startMs: number;
  endMs: number;
  text: string;
  speaker: string;
  translation: string | null;
}

export type UndoEntry =
  | { kind: 'update'; lineId: string; patch: LinePatch }
  | { kind: 'delete'; lineId: string }
  | { kind: 'insert'; afterLineId: string | null; line: InsertContent }
  /** split/merge can't be inverted client-side (ids/timing are the server's) — blocks undo */
  | { kind: 'barrier'; op: 'split' | 'merge' };

export const UNDO_LIMIT = 20;

const PATCH_FIELDS = ['text', 'translation', 'speaker', 'startMs', 'endMs'] as const;

/**
 * The inverse of a committed mutation, or null when it isn't undoable (split/merge, or an
 * update that changed nothing). `before`/`after` are the line lists around the mutation.
 */
export function inverseOf(
  op: MutationOp,
  before: readonly Line[],
  after: readonly Line[],
): UndoEntry | null {
  switch (op.kind) {
    case 'update': {
      if (!op.lineId) return null;
      const b = before.find((l) => l.id === op.lineId);
      const a = after.find((l) => l.id === op.lineId);
      if (!b || !a) return null;
      const patch: LinePatch = {};
      let changed = false;
      for (const f of PATCH_FIELDS) {
        if (a[f] !== b[f]) {
          (patch as Record<string, unknown>)[f] = b[f];
          changed = true;
        }
      }
      return changed ? { kind: 'update', lineId: op.lineId, patch } : null;
    }
    case 'insert': {
      const beforeIds = new Set(before.map((l) => l.id));
      const added = after.find((l) => !beforeIds.has(l.id));
      return added ? { kind: 'delete', lineId: added.id } : null;
    }
    case 'delete': {
      const afterIds = new Set(after.map((l) => l.id));
      const i = before.findIndex((l) => !afterIds.has(l.id));
      if (i === -1) return null;
      const gone = before[i];
      // re-insert after the nearest surviving predecessor (it may itself have been removed)
      let afterLineId: string | null = null;
      for (let j = i - 1; j >= 0; j--) {
        if (afterIds.has(before[j].id)) {
          afterLineId = before[j].id;
          break;
        }
      }
      return {
        kind: 'insert',
        afterLineId,
        line: {
          startMs: gone.startMs,
          endMs: gone.endMs,
          text: gone.text,
          speaker: gone.speaker,
          translation: gone.translation,
        },
      };
    }
    case 'split':
    case 'merge':
      return null;
  }
}

/** Push onto the undo stack, keeping the last `limit` entries. */
export function pushUndo(
  stack: readonly UndoEntry[],
  entry: UndoEntry,
  limit = UNDO_LIMIT,
): UndoEntry[] {
  const next = [...stack, entry];
  return next.length > limit ? next.slice(next.length - limit) : next;
}

/** Why the Undo button is disabled (null = enabled). */
export function undoDisabledReason(stack: readonly UndoEntry[]): string | null {
  const top = stack[stack.length - 1];
  if (!top) return 'Nothing to undo';
  if (top.kind === 'barrier')
    return `A ${top.op} can't be undone — restore a snapshot to go back further`;
  return null;
}

/** Number of undoable entries above the top-most barrier. */
export function undoableCount(stack: readonly UndoEntry[]): number {
  let n = 0;
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i].kind === 'barrier') break;
    n++;
  }
  return n;
}

export function describeUndo(entry: UndoEntry): string {
  switch (entry.kind) {
    case 'update': {
      const fields = Object.keys(entry.patch);
      if (fields.length === 1) {
        const f = fields[0];
        if (f === 'text') return 'Undo text edit';
        if (f === 'translation') return 'Undo translation edit';
        if (f === 'speaker') return 'Undo speaker change';
        return 'Undo timing change';
      }
      return 'Undo edit';
    }
    case 'delete':
      return 'Undo insert';
    case 'insert':
      return 'Undo delete (the line comes back with a new id)';
    case 'barrier':
      return `Can't undo a ${entry.op}`;
  }
}

// ---------------------------------------------------------------- row ops (validation)

export const NUDGE_MS = 100;

/** ±delta, never below zero. */
export function nudge(ms: number, delta: number): number {
  return Math.max(0, Math.round(ms + delta));
}

/**
 * Split validation only (the server interpolates the timing): both halves must be non-blank.
 * `atChar` is the KA textarea's selectionStart.
 */
export function splitAtCursor(
  text: string,
  atChar: number,
): { ok: true; first: string; second: string } | { ok: false; reason: string } {
  if (atChar <= 0 || atChar >= text.length)
    return { ok: false, reason: 'Put the cursor inside the Georgian text, then split' };
  const first = text.slice(0, atChar).trim();
  const second = text.slice(atChar).trim();
  if (!first || !second)
    return { ok: false, reason: 'Both halves need some text — move the cursor' };
  return { ok: true, first, second };
}

/** Merge is with the NEXT row, so the last row can't merge. */
export function canMerge(lines: readonly Line[], idx: number): boolean {
  return idx >= 0 && idx < lines.length - 1;
}

export const INSERT_DEFAULT_LEN_MS = 1500;
export const MIN_LINE_MS = 100;

/**
 * Where a row inserted after `after` goes: starts at its end, lasts 1.5 s but stops at the next
 * row's start and the video's end. `ok:false` when there is no 100 ms of room (the server would
 * 422 on overlap — say so before the round trip).
 */
export function insertDefaults(
  after: Line | null,
  next: Line | null,
  durationMs: number | null,
): { ok: true; startMs: number; endMs: number } | { ok: false; reason: string } {
  const startMs = after ? after.endMs : 0;
  let endMs = startMs + INSERT_DEFAULT_LEN_MS;
  if (next) endMs = Math.min(endMs, next.startMs);
  if (durationMs != null && durationMs > 0) endMs = Math.min(endMs, durationMs);
  if (endMs - startMs < MIN_LINE_MS)
    return {
      ok: false,
      reason: 'No room between this line and the next — nudge an end earlier first',
    };
  return { ok: true, startMs, endMs };
}

/** Speakers seen so far, in first-appearance order (the speaker field's datalist). */
export function knownSpeakers(lines: readonly Line[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const l of lines) {
    const s = l.speaker.trim();
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
}

/** Rows with no English yet — the "Translate missing (n)" count. */
export function missingTranslations(lines: readonly Line[]): number {
  let n = 0;
  for (const l of lines) if (!l.translation || !l.translation.trim()) n++;
  return n;
}

/**
 * The transcript's `translationStatus` as the server recomputes it: 'complete' when every row
 * has a non-empty translation, 'none' when no row does, else 'partial' (an empty transcript
 * counts as 'none'). Mutation responses don't carry it, so the client mirrors it after a commit.
 */
export function translationStatusOf(lines: readonly Line[]): TranslationStatus {
  let has = 0;
  for (const l of lines) if (l.translation !== null && l.translation !== '') has++;
  if (has === 0) return 'none';
  return has === lines.length ? 'complete' : 'partial';
}

/** The fields of a row draft that differ from the row — what a blur commits. */
export function draftPatch(
  line: Pick<Line, 'text' | 'translation' | 'speaker'>,
  draft: { text: string; translation: string; speaker: string },
): LinePatch {
  const patch: LinePatch = {};
  if (draft.text !== line.text) patch.text = draft.text;
  const tr = draft.translation.trim() === '' ? null : draft.translation;
  if (tr !== (line.translation ?? null)) patch.translation = tr;
  if (draft.speaker.trim() !== line.speaker) patch.speaker = draft.speaker.trim();
  return patch;
}

// ---------------------------------------------------------------- time fields

/** `m:ss.mmm` (`h:mm:ss.mmm` past an hour) — the editable form of a timestamp. */
export function fmtTimeMs(ms: number): string {
  const v = Math.max(0, Math.round(ms));
  const h = Math.floor(v / 3_600_000);
  const m = Math.floor((v % 3_600_000) / 60_000);
  const s = Math.floor((v % 60_000) / 1000);
  const frac = v % 1000;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}.${String(frac).padStart(3, '0')}`;
}

/**
 * Parse what a learner types into a time field: `m:ss`, `m:ss.mmm`, `h:mm:ss.mmm`, `ss.s`,
 * or plain seconds. Comma accepted as the decimal mark. null when it isn't a time.
 */
export function parseTimeInput(s: string): number | null {
  const m = /^\s*(?:(\d+):)?(?:(\d+):)?(\d+)(?:[.,](\d{1,3}))?\s*$/.exec(s);
  if (!m) return null;
  const [, a, b, c, frac] = m;
  let h = 0;
  let min = 0;
  const sec = Number(c);
  if (a !== undefined && b !== undefined) {
    // h:mm:ss
    h = Number(a);
    min = Number(b);
    if (min > 59 || sec > 59) return null;
  } else if (a !== undefined) {
    // m:ss (minutes may run past 59)
    min = Number(a);
    if (sec > 59) return null;
  }
  const ms = frac ? Number(frac.padEnd(3, '0')) : 0;
  return ((h * 60 + min) * 60 + sec) * 1000 + ms;
}

// ---------------------------------------------------------------- errors / conflicts

export type EditErrorClass =
  | { kind: 'stale'; revision: number | null }
  | { kind: 'job'; job: Job | null }
  | { kind: 'forbidden'; message: string }
  | { kind: 'gone' }
  | { kind: 'validation'; message: string }
  | { kind: 'budget'; message: string }
  | { kind: 'other'; message: string };

/** The problem bodies line/transcript routes answer with (extras are top-level, per `problem()`). */
type EditProblem = Problem & { revision?: unknown; job?: unknown; extra?: Record<string, unknown> };

/**
 * Turn a route's Problem into what the editor should do: 409 + `revision` → the transcript
 * changed elsewhere (reload); 409 + `job` → a job is running (lock); 403 → read-only; 404 → the
 * row is gone; 422 → the calm validation line; 429 → the budget line.
 */
export function classifyEditError(p: Problem | null | undefined): EditErrorClass {
  if (!p) return { kind: 'other', message: 'Something went wrong — try again' };
  const ep = p as EditProblem;
  const detail = p.detail?.trim();
  switch (p.status) {
    case 409: {
      const rev = ep.revision ?? ep.extra?.revision;
      const job = ep.job ?? ep.extra?.job;
      if (typeof rev === 'number') return { kind: 'stale', revision: rev };
      if (job && typeof job === 'object') return { kind: 'job', job: job as Job };
      if (/job/i.test(p.title) || /job/i.test(detail ?? '')) return { kind: 'job', job: null };
      return { kind: 'stale', revision: null };
    }
    case 403:
      return { kind: 'forbidden', message: detail || 'This transcript is read-only for you' };
    case 404:
      return { kind: 'gone' };
    case 422: {
      const first = p.errors?.[0]?.message;
      return { kind: 'validation', message: detail || first || 'That change isn’t valid' };
    }
    case 429:
      return {
        kind: 'budget',
        message: detail || 'The AI helper is resting for a bit — cached glosses still work',
      };
    default:
      return { kind: 'other', message: detail || p.title || 'Something went wrong — try again' };
  }
}

/** Jobs lock editing while queued/running (409 on every line write). */
export function jobLocks(job: Pick<Job, 'status'> | null | undefined): boolean {
  return !!job && (job.status === 'queued' || job.status === 'running');
}

export function jobLockLine(job: Pick<Job, 'kind'> | null | undefined): string {
  const what =
    job?.kind === 'caption_cleanup'
      ? 'cleaned up'
      : job?.kind === 'translate'
        ? 'translated'
        : 'worked on';
  return `Editing is locked while the transcript is being ${what}`;
}

/** Human name of a job kind for the finished-while-dirty banner. */
export function jobNoun(kind: string | null | undefined): string {
  if (kind === 'caption_cleanup') return 'Cleanup';
  if (kind === 'translate') return 'Translation';
  return 'The job';
}

/**
 * The job-finished-vs-dirty rule (Workspace.jsx:463-478, as an inline banner): when a job the
 * editor watched goes from locked to done, a learner with an in-progress edit is asked before
 * their draft is discarded; with nothing in progress the fresh transcript is simply loaded.
 */
export function jobTransition(
  prev: Pick<Job, 'id' | 'kind' | 'status'> | null,
  next: Pick<Job, 'id' | 'kind' | 'status'> | null,
): { kind: 'finished'; jobKind: string; ok: boolean } | null {
  if (!prev || !jobLocks(prev)) return null;
  if (next && next.id === prev.id && jobLocks(next)) return null;
  const status = next && next.id === prev.id ? next.status : 'done';
  return { kind: 'finished', jobKind: prev.kind, ok: status !== 'error' };
}

export type ConflictChoice = 'reload' | 'keep';

/**
 * What arriving lines should do given the learner's state: adopt when nothing is in progress
 * (or the learner already chose reload); otherwise hold them and ask.
 */
export function incomingLinesAction(
  incomingRevision: number,
  localRevision: number,
  dirty: boolean,
  adoptOnArrival: boolean,
): 'ignore' | 'adopt' | 'ask' {
  if (incomingRevision <= localRevision) return 'ignore';
  if (!dirty || adoptOnArrival) return 'adopt';
  return 'ask';
}

// ---------------------------------------------------------------- scroll anchoring (pure part)

export interface RowRect {
  idx: number;
  /** offsets relative to the scroll box's top edge */
  top: number;
  bottom: number;
}

/**
 * Which row to hold still across a layout change (Transcript.jsx:119-166): the first of
 * `preferred` that is on-screen, else the visible row nearest the viewport centre. Returns the
 * row and its current top offset; the component restores that offset after the DOM changes.
 */
export function pickAnchor(
  rows: readonly RowRect[],
  viewportHeight: number,
  preferred: readonly number[] = [],
): { idx: number; top: number } | null {
  const visible = (r: RowRect) => r.bottom > 0 && r.top < viewportHeight;
  for (const p of preferred) {
    const r = rows.find((x) => x.idx === p);
    if (r && visible(r)) return { idx: r.idx, top: r.top };
  }
  const mid = viewportHeight / 2;
  let best: RowRect | null = null;
  let bestDist = Infinity;
  for (const r of rows) {
    if (!visible(r)) continue;
    const d = Math.abs((r.top + r.bottom) / 2 - mid);
    if (d < bestDist) {
      bestDist = d;
      best = r;
    }
  }
  return best ? { idx: best.idx, top: best.top } : null;
}
